import { ActionTable } from '@/components';
import { useATSelection } from '@/components/table/hooks';
import { useAppSelector } from '@/state';
import { RfiApi } from '@/state/api';
import { RfiSubmittalsType } from '@/types';
import { rfiLogImpact, rfiLogStatus } from '@/types/enums';
import { permissionsKeys } from '@/types/permissionsType';
import { toDateInputValue } from '@/utils/dateFields';
import { PermissionCheck } from '@/utils/PermissionCheck';
import { Box, Button, Center, Flex, Text } from '@chakra-ui/react';
import { Card, OverlayDialog, PopoverLazy, toasterApi } from '@ui';
import { useEffect, useState } from 'react';
import ExportRfiLogModal from './ExportRfiLogModal';
import { getRfiBulkExportFilename } from './ExportRfiLogModal/exportUtils';
import ManageCard from './ManageCard';
import RfiLogDetails from './RfiLogDetails';

const STATUS_ROW_BG: Record<string, string | undefined> = {
  closed: 'rgba(34, 197, 94, 0.22)',
  submitted: undefined,
  resubmittal_required: 'rgba(239, 68, 68, 0.24)',
  void: 'rgba(168, 85, 247, 0.20)',
};

const RfiLogs = () => {
  const [showSlideForRfiItem, setShowSlideForRfiItem] = useState<{
    rfi_id: number;
  } | null>(null);

  const selectedProject = useAppSelector(
    (state) => state.project.selectedProject,
  );
  const { selection } = useATSelection();
  const [postRfiLogsExportLog] = RfiApi.Api.usePostRfiLogsExportLogMutation();

  useEffect(() => {
    selection.clearImmediate();
  }, [selectedProject?.id, selection]);

  const { data: allRfis, isLoading: isLoadingAllRfis } =
    RfiApi.Api.useGetAllRfiLogsQuery(
      {
        query: {
          project: selectedProject?.id as number,
        },
      },
      {
        skip: !selectedProject?.id,
      },
    );

  if (showSlideForRfiItem) {
    return (
      <Flex flexDirection="column" gapY={4}>
        <RfiLogDetails
          rfi_log_id={showSlideForRfiItem?.rfi_id}
          onClose={() => setShowSlideForRfiItem(null)}
        />
      </Flex>
    );
  }

  const getRowBg = (item?: RfiLogsTableColumnType) => {
    if (!item?.status) return undefined;
    return STATUS_ROW_BG[item.status] ?? undefined;
  };

  const renderCell = (
    props: ActionTable.RenderCellType<
      RfiLogsTableColumnType,
      { actions: null }
    >,
  ) => {
    const { colKey, cellValue, rowValue } = props;

    switch (colKey) {
      case 'due_date':
        return cellValue ? <Text>{toDateInputValue(cellValue)}</Text> : null;

      case 'is_overdue':
        return cellValue ? <Text>Yes</Text> : null;

      case 'status':
        return (
          <Text textTransform="capitalize">
            {rfiLogStatus?.find((s) => s.value === cellValue)?.label ?? cellValue}
          </Text>
        );

      case 'impact':
        return (
          <Text textTransform="capitalize">
            {rfiLogImpact?.find((s) => s.value === cellValue)?.label ?? cellValue}
          </Text>
        );

      case 'actions': {
        const item = rowValue;
        return (
          <Button
            onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
              e.stopPropagation();
              if (item) setShowSlideForRfiItem({ rfi_id: item.id });
            }}
          >
            View
          </Button>
        );
      }

      default:
        return cellValue?.value ?? cellValue ?? '';
    }
  };

  return (
    <Flex flexDirection="column" gapY={4}>
      <Card>
        <Flex justifyContent="space-between" alignItems="center">
          <Text>RFIs</Text>
          <Flex gap={2}>
            <PermissionCheck permissions={[permissionsKeys.view_rfilog]}>
              {!selection?.getArray().length ? (
                <PopoverLazy
                  button={<Button variant="outline">Export Log</Button>}
                  popOverTitle=""
                >
                  <Flex flexDirection="column" gapY={4}>
                    <Text fontWeight="bold" fontSize="md">
                      Cannot Export
                    </Text>
                    <Text>
                      Please select RFIs from the table below to export.
                    </Text>
                  </Flex>
                </PopoverLazy>
              ) : (
                <Button
                  variant="outline"
                  onClick={() => {
                    const selectedIds = selection.map((row) => row.id);
                    const selected = (allRfis?.data ?? []).filter((rfi) =>
                      selectedIds.includes(rfi.id),
                    );

                    OverlayDialog.open('exportRfiLog', {
                      overlayId: 'exportRfiLog',
                      title: `Export RFI Log (${selected.length})`,
                      content: (
                        <ExportRfiLogModal
                          selectedRfis={selected}
                          onExport={async (rows) => {
                            let blob: Blob;
                            try {
                              blob = await postRfiLogsExportLog({
                                body: { rows },
                              }).unwrap();
                            } catch {
                              // The global RTK error middleware displays API failures.
                              return;
                            }

                            let objectUrl: string | undefined;
                            let anchor: HTMLAnchorElement | undefined;
                            try {
                              objectUrl = window.URL.createObjectURL(blob);
                              anchor = document.createElement('a');
                              anchor.href = objectUrl;
                              anchor.download = getRfiBulkExportFilename();
                              document.body.appendChild(anchor);
                              anchor.click();
                              OverlayDialog.close('exportRfiLog');
                              toasterApi({
                                type: 'success',
                                status: 200,
                                code: 'Exported',
                                message: 'RFI log exported successfully',
                              });
                            } catch (error: any) {
                              toasterApi({
                                type: 'error',
                                status: 500,
                                code: 'DownloadError',
                                message:
                                  error?.message ||
                                  'The RFI log was generated but could not be downloaded.',
                              });
                            } finally {
                              anchor?.remove();
                              if (objectUrl) {
                                window.URL.revokeObjectURL(objectUrl);
                              }
                            }
                          }}
                        />
                      ),
                    });
                  }}
                >
                  Export Log
                </Button>
              )}
            </PermissionCheck>

            <Button
              onClick={() => {
                OverlayDialog.open('addRfiLog', {
                  overlayId: 'addRfiLog',
                  title: 'Create RFI',
                  content: <ManageCard overlayId="addRfiLog" isNew />,
                });
              }}
            >
              Add RFI
            </Button>
          </Flex>
        </Flex>

        <Center>
          <Flex flexDirection="column" gapY={2} minW={0}>
            <ActionTable.FilterBar
              disabled={!selectedProject?.id}
              filterConfigComponents={filterConfigComponents}
            />
            <Box
              key={`rfi-log-table-${selectedProject?.id}`}
              display="flex"
              flexDirection="row"
              gap={5}
            >
              <Box h="100%" my={5}>
                <ActionTable<RfiLogsTableColumnType, { actions: null }>
                  width="91.5vw"
                  columns={columns}
                  columnToUseAsRowKey="id"
                  items={allRfis?.data ?? []}
                  isPaginated={false}
                  isPaginatedAsync={false}
                  totalItemsCount={allRfis?.data?.length ?? 0}
                  isSortable
                  isFilterable
                  isShowActionBar={false}
                  height="55vh"
                  rowHeight={100}
                  renderCell={renderCell}
                  renderRow={({ rowValue }) => {
                    const bg = getRowBg(rowValue);

                    return {
                      rowProps: {
                        bg: bg ?? 'transparent',
                        _hover: { bg: bg ?? 'primary.muted' },
                      },
                    } as any;
                  }}
                  isLoading={isLoadingAllRfis}
                  isMultipleSelection
                  isCheckbox
                  onRowDoubleClick={(row) =>
                    setShowSlideForRfiItem({ rfi_id: row.id })
                  }
                />
              </Box>
            </Box>
          </Flex>
        </Center>
      </Card>
    </Flex>
  );
};

export default () => (
  <ActionTable.Provider>
    <RfiLogs />
  </ActionTable.Provider>
);

type RfiLogsTableColumnType =
  RfiSubmittalsType.RfiLog.Get['response']['data'][number];

const columns: ActionTable.ColumnsConfigType<
  RfiLogsTableColumnType,
  { actions: null }
> = [
  {
    id: {
      label: 'Id',
      width: '100px',
    },
  },
  {
    rfi_number: {
      label: 'Rfi Number',
      width: '120px',
    },
  },
  {
    subject: {
      label: 'Subject',
      width: '220px',
    },
  },
  {
    zone_level: {
      label: 'Zone Level',
      width: '120px',
    },
  },
  {
    ball_in_court_display: {
      label: 'Ball In Court',
      width: '160px',
    },
  },
  {
    status: {
      label: 'Status',
      width: '180px',
    },
  },
  {
    impact: {
      label: 'Impact',
      width: '120px',
    },
  },
  {
    due_date: {
      label: 'Due Date',
      width: '150px',
    },
  },
  {
    days_open: {
      label: 'Days Open',
      width: '110px',
    },
  },
  {
    spec_section: {
      label: 'Spec Section',
      width: '150px',
    },
  },
  {
    refs: {
      label: 'Refs',
      width: '120px',
    },
  },
  {
    is_overdue: {
      label: 'Overdue',
      width: '100px',
    },
  },
  {
    actions: {
      label: 'Actions',
      width: '100px',
    },
  },
];

const filterConfigComponents: ActionTable.FilterConfigComponentType<RfiLogsTableColumnType>[] =
  [
    {
      filterComponent: 'input',
      filterComponentLabel: 'Rfi Number',
      filterConfigItems: [
        {
          colKeys: ['rfi_number'],
          value: '',
          operator: 'contains',
        },
      ],
    },
    {
      filterComponent: 'input',
      filterComponentLabel: 'Subject',
      filterConfigItems: [
        {
          colKeys: ['subject'],
          value: '',
          operator: 'contains',
        },
      ],
    },
    {
      filterComponent: 'select',
      filterComponentLabel: 'Status',
      filterConfigItems: rfiLogStatus.map((status) => ({
        colKeys: ['status'],
        value: status?.label,
        operator: 'contains',
      })),
    },
    {
      filterComponent: 'select',
      filterComponentLabel: 'Impact',
      filterConfigItems: rfiLogImpact.map((status) => ({
        colKeys: ['impact'],
        value: status?.label,
        operator: 'contains',
      })),
    },
  ];
